import { Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, delay, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';
import { environment } from '@env/environment';
import { Logger } from '@app/core/logging/logger';
import { NotificationEvent } from '@app/core/enums/notification.enum';
import { NotifyUser } from '@app/core/models/notify-user.model';
import { AuthService } from '@app/core/auth/auth.service';
import { HttpService } from '@app/core/http/http.service';
import { InspectionService } from '@app/core/services/inspection.service';
import { LocationService } from '@app/core/services/location.service';
import { User } from '@app/core/models/user.model';
import { NotificationPayload } from '@app/core/models/notification-payload.model';
import { NotifyInputModel } from '@app/core/models/notify-input.model';
import { NotificationListener } from '@app/core/models/notification-listener.model';
import _ from 'lodash';

@Injectable({ providedIn: 'root' })
export class NotifyService {
    private locationService = inject(LocationService);
    private authService = inject(AuthService);
    private inspectionService = inject(InspectionService);
    private logger = inject(Logger);
    private httpService = inject(HttpService);

    private hubConnection: HubConnection;
    private subject = new Subject<NotificationPayload>();
    private currentlyListeningTo: NotificationListener[] = [];
    private connectionClosedInError = false;
    private _hubIsConnected = false;
    private _user: User;
    private _hubIsConnectedSubject = new ReplaySubject<boolean>();

    private createHub() {
        this.hubConnection = new HubConnectionBuilder()
            .withUrl(`${environment.notifyHub}`,
                <IHttpConnectionOptions>{
                    transport: HttpTransportType.WebSockets,
                    accessTokenFactory: () => this.authService.getAccessToken
                })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    return Math.random() * 5000 + 5000;
                }
            })
            .configureLogging(LogLevel.Information)
            .build();
        this.hubConnection.onclose(err => {
            this.logger.error(new Error('Hub OnClose event'), { error: err });
        });

        this.hubConnection.onreconnected(connectionId => {
            this.logger.info('Hub Reconnected', { connectionId });
            this.resubscribeToGroups();
        });
    }

    private startConnection(retryCount: number = 0): Promise<void> {
        if (this._hubIsConnected) {
            return Promise.resolve();
        }

        return this.hubConnection.start()
            .then(() => {
                this._hubIsConnected = true;
                this._hubIsConnectedSubject.next(true);
                this.logger.info('Hub is connected');

                if (this.connectionClosedInError) {
                    this.resubscribeToGroups();
                    this.connectionClosedInError = false;
                }
            })
            .catch(async () => {
                this._hubIsConnected = false;
                this.connectionClosedInError = true;
                retryCount = retryCount + 1;
                const waitTime = retryCount >= 5 ? 5000 : retryCount * 1000;
                delay(waitTime);
                this.logger.warn(`Retry hub connection failed. Retry count: ${retryCount}.`);
                return this.startConnection(retryCount);
            });
    }

    private setupHubEvents() {
        this.hubConnection.onclose(async err => {
            this._hubIsConnected = false;

            if (!err) {
                this.logger.info('Hub disconnected cleanly');
                return;
            }

            this.connectionClosedInError = true;

            this.logger.error(new Error('Hub connection was closed with error'), {
                error: err,
                inspection: this.inspectionService.getCurrentInspection()
            });

            this.startConnection();
        });

        this.hubConnection.on(NotificationEvent.InspectionsUpdated, (user: NotifyUser, payload?: any) =>
            this.notificationReceived(NotificationEvent.InspectionsUpdated, user, payload));

        this.hubConnection.on(NotificationEvent.InspectionUpdated, (user: NotifyUser, payload?: any) =>
            this.notificationReceived(NotificationEvent.InspectionUpdated, user, payload));

        this.hubConnection.on(NotificationEvent.CategoryUpdated, (user: NotifyUser, payload?: any) =>
            this.notificationReceived(NotificationEvent.CategoryUpdated, user, payload));
    }

    private notificationReceived(notification: NotificationEvent, user: NotifyUser, payload?: any) {
        this.subject.next({
            type: notification,
            user: user,
            payload: payload
        });

        this.logger.debug(`${notification} notification received`, { ...user, ...payload });
    }

    private listenForChangesToInspections() {
        return this.hubConnection.invoke('ListenForChangesToInspections',
            this.user.tenantId,
            this.locationService.currentLocation.locationId);
    }

    private stopListeningForChangesToInspections() {
        return this.hubConnection.invoke('StopListeningForChangesToInspections',
            this.user.tenantId,
            this.locationService.currentLocation.locationId);
    }

    private listenForChangesToInspection(inspectionId: string) {
        return this.hubConnection.invoke('ListenForChangesToInspection',
            this.user.tenantId,
            this.locationService.currentLocation.locationId,
            inspectionId);
    }

    private stopListeningForChangesToInspection(inspectionId: string) {
        return this.hubConnection.invoke('StopListeningForChangesToInspection',
            this.user.tenantId,
            this.locationService.currentLocation.locationId,
            inspectionId);
    }

    private listenForChangesToCategory(inspectionId: string, categoryId: string) {
        return this.hubConnection.invoke('ListenForChangesToCategory',
            this.user.tenantId,
            this.locationService.currentLocation.locationId,
            inspectionId,
            categoryId,
            this.user.name);
    }

    private stopListeningForChangesToCategory(inspectionId: string, categoryId: string) {
        return this.hubConnection.invoke('StopListeningForChangesToCategory',
            this.user.tenantId,
            this.locationService.currentLocation.locationId,
            inspectionId,
            categoryId,
            this.user.name);
    }

    private async listenForChangesTo(notificationEvent: NotificationEvent, params: NotifyInputModel = null): Promise<any> {
        const addToListeningArray = (parameters: NotifyInputModel = null) => {
            this.logger.debug('Listening for changes to ' + notificationEvent + ' at ' + this.locationService.currentLocation.name);
            if (this.currentlyListeningTo.find(listener => listener.event === notificationEvent &&
                _.isEqual(listener.params, params)) == null) {
                this.currentlyListeningTo.push({
                    event: notificationEvent,
                    params: parameters
                });
            }
        };

        switch (notificationEvent) {
            case NotificationEvent.InspectionsUpdated:
                return this
                    .listenForChangesToInspections()
                    .then(() => addToListeningArray(params));
            case NotificationEvent.InspectionUpdated:
                const inspectionId = this.inspectionService.getCurrentInspection()?.inspectionId
                return this
                    .listenForChangesToInspection(inspectionId)
                    .then(() => addToListeningArray({
                        inspectionId: inspectionId
                    }));
            case NotificationEvent.CategoryUpdated: {
                return this.listenForChangesToCategory(params.inspectionId, params.categoryId).then(() => addToListeningArray(params));
            }
        }
    }

    private async stopListeningForChangesTo(notificationEvent, params: NotifyInputModel = null) {
        const removeFromListeningListening = (parameters: NotifyInputModel = null) => {
            this.logger.debug('Stopped listening for changes to ' + notificationEvent + ' at ' +
                this.locationService.currentLocation.name);
            this.currentlyListeningTo = this.currentlyListeningTo.filter(i =>
                i.event !== notificationEvent ||
                !_.isEqual(i.params, parameters));
        };

        switch (notificationEvent) {
            case NotificationEvent.InspectionsUpdated:
                return this.stopListeningForChangesToInspections()
                    .then(() => removeFromListeningListening());
            case NotificationEvent.InspectionUpdated:
                const inspectionId = this.inspectionService.getCurrentInspection()?.inspectionId;
                return this.stopListeningForChangesToInspection(inspectionId)
                    .then(() => removeFromListeningListening({
                        inspectionId: inspectionId
                    }));
            case NotificationEvent.CategoryUpdated: {
                return this
                    .stopListeningForChangesToCategory(params.inspectionId, params.categoryId)
                    .then(() => removeFromListeningListening(params))
            }
        }
    }

    private resubscribeToGroups = () =>
        this.currentlyListeningTo.forEach(listener => this.listenForChangesTo(listener.event, listener.params));

    private publishNotification(notificationEvent: NotificationEvent, params: NotifyInputModel = null) {
        return this.subject.asObservable().pipe(
            filter(i => {
                const allowEvent = i.type === notificationEvent;
                const silentCurrentUser = notificationEvent === NotificationEvent.InspectionUpdated && i.user.name === this.user.name;

                if (!allowEvent || silentCurrentUser) {
                    return false;
                }

                if (params) {
                    switch (i.type) {
                        case NotificationEvent.CategoryUpdated:
                            if (params.categoryId !== i.user.categoryId) {
                                return false;
                            }
                            break;
                        case NotificationEvent.InspectionUpdated:
                            if (params.inspectionId !== i.user.inspectionId) {
                                return false;
                            }
                            break;

                        case NotificationEvent.InspectionsUpdated:
                            break;
                    }
                }

                this.logger.debug('Published event for ' + i.type);

                return true;
            }));
    }

    public async initNotificationService() {
        if (this._hubIsConnected) {
            return Promise.resolve();
        }

        this.createHub();

        await Promise.all([
            this.getCurrentUser().toPromise(),
            this.startConnection()
        ]);

        this.setupHubEvents();
        this.logger.info('Initialised Hub service');
    }

    /**
     * Stops the SignalR hub connection and resets all connection state.
     * Must be called on logout to prevent the hub from receiving events
     * for an unauthenticated session (CS-7.2).
     */
    public async disconnect(): Promise<void> {
        if (!this.hubConnection) {
            return;
        }

        this.currentlyListeningTo = [];
        this._hubIsConnected = false;
        this._hubIsConnectedSubject = new ReplaySubject<boolean>();

        await this.hubConnection.stop();
    }

    /**
     * Subscribe to notifications by type
     *
     * @param {NotificationEvent} notificationEvent
     * @returns {Observable<NotifyUser>} The user who trigger the event
     * member of NotifyService
     */
    public when(notificationEvent: NotificationEvent, params: any = null): Observable<NotificationPayload> {
        return this._hubIsConnectedSubject.pipe(filter((isConnected) => isConnected))
            .pipe(
                switchMap(() => this.listenForChangesTo(notificationEvent, params)),
                switchMap(() => this.publishNotification(notificationEvent, params)),
                finalize(() => this.stopListeningForChangesTo(notificationEvent, params)));
    }

    public get user(): User {
        return this._user;
    }

    private getCurrentUser(): Observable<User> {
        if (this.user) {
            return of(this.user);
        }

        return this.httpService.get(environment.apiEndpoint + 'me').pipe(
            map(res => (<any>res.body).data as User),
            tap(user => this._user = Object.assign({}, user)),
            catchError(error => throwError(() => error)));
    }
}
